/**
 * Runtime configuration schema.
 *
 * NOTHING in the applications may hardcode a domain, account ID, region or
 * resource name. Every environment-specific value flows through
 * `FrondConfig`, which is injected at deploy time (see infra/):
 *
 *  - `apps/app` and `apps/web` fetch `/config.json` from their own origin at
 *    startup (a file the CDK renders per environment), and also accept
 *    `window.__FROND_CONFIG__` for embedding.
 *  - `services/api` reads the same shape from SSM Parameter Store.
 *
 * This is what makes the product trivially redeployable under any domain
 * ("frond.cntnus.app" is only a placeholder) and open-source friendly.
 */

export type EnvironmentName = "dev" | "stage" | "prod";

export interface FrondConfig {
  /** Schema version; bump on breaking changes. */
  version: 1;
  environment: EnvironmentName;
  /** Human-facing product name (placeholder: "Frond"). */
  productName: string;
  auth: {
    region: string;
    userPoolId: string;
    userPoolClientId: string;
    /** Full domain of the Cognito Hosted UI (or of the API for JWT issuer). */
    issuer: string;
    /** OAuth redirect URI registered in the client. */
    redirectUri: string;
    /** OAuth scopes for the hosted UI (default: openid email profile). */
    scopes: string[];
  };
  api: {
    /** Base URL of the API Gateway stage, e.g. https://api.<domain>/v1 */
    baseUrl: string;
  };
  storage: {
    region: string;
    bucket: string;
  };
  /** Optional, privacy-preserving error reporting (no PHI ever sent). */
  observability?: {
    sentryDsn?: string;
  };
  features?: {
    collaboration?: boolean;
    publishing?: boolean;
  };
}

export const DEFAULT_SCOPES = ["openid", "email", "profile"];

export const EMPTY_CONFIG: FrondConfig = {
  version: 1,
  environment: "dev",
  productName: "Frond",
  auth: {
    region: "",
    userPoolId: "",
    userPoolClientId: "",
    issuer: "",
    redirectUri: "",
    scopes: DEFAULT_SCOPES,
  },
  api: { baseUrl: "" },
  storage: { region: "", bucket: "" },
};

/** Load config from a window global (build-time embed) if present. */
export function configFromWindow(): Partial<FrondConfig> | null {
  if (typeof window === "undefined") return null;
  const g = window as unknown as { __FROND_CONFIG__?: Partial<FrondConfig> };
  return g.__FROND_CONFIG__ ?? null;
}

/**
 * Load and validate config: window global first, then `/config.json` from
 * the current origin (works under any domain the site is served from).
 */
export async function loadConfig(): Promise<FrondConfig> {
  const embedded = configFromWindow();
  if (embedded && embedded.version === 1 && embedded.auth) {
    return validate({ ...EMPTY_CONFIG, ...embedded });
  }
  const res = await fetch(`${window.location.origin}/config.json`, { cache: "no-store" });
  if (!res.ok) {
    throw new Error(`Unable to load runtime configuration (${res.status})`);
  }
  const data: unknown = await res.json();
  return validate(data);
}

export function validate(data: unknown): FrondConfig {
  if (typeof data !== "object" || data === null) {
    throw new Error("Invalid configuration: not an object");
  }
  const cfg = data as Partial<FrondConfig>;
  if (cfg.version !== 1) throw new Error("Invalid configuration: unsupported version");
  const required: Array<[keyof FrondConfig, string]> = [
    ["environment", "environment"],
    ["auth", "auth"],
    ["api", "api"],
    ["storage", "storage"],
  ];
  for (const [key, label] of required) {
    if (cfg[key] === undefined) throw new Error(`Invalid configuration: missing "${label}"`);
  }
  if (!cfg.auth?.userPoolId || !cfg.auth?.userPoolClientId || !cfg.auth?.issuer) {
    throw new Error("Invalid configuration: auth is incomplete");
  }
  if (!cfg.api?.baseUrl) throw new Error("Invalid configuration: api.baseUrl missing");
  return cfg as FrondConfig;
}
